import { PathStyleExtension } from '@deck.gl/extensions';
import distance from '@turf/distance';
import { point as turfPoint } from '@turf/helpers';
import {
  Layer,
  PathLayer,
  PickInfo,
  PolygonLayer,
  Position,
  ScatterplotLayer,
  TextLayer,
} from 'deck.gl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IInsituPoint } from '../../../../api/endpoints/FileEndPoint';
import { useMapLayerStore } from '../../../../store/layerStore';
import useLegendStore from '../../../../store/legendStore';
import useMapStore from '../../../../store/mapStore';
import { useActiveMarker, useMarkerStore } from '../../../../store/markerStore';
import { usePointStore } from '../../../../store/pointStore';
import { ILabelLayer, IPolygonLayer } from '../../../../types/map';
import { DatasetType, IMarker } from '../../../../types/marker';
import { ConvertHexToRGBA, selectedColor } from '../../../../utils/common';
import { toFixed } from '../../../../utils/toFixed';
import { IWmsProps } from '../Layer/DatasetMesh';
import {
  DatasetZoneLayer,
  FeatureCollectionLayer,
  GlobalDatasetZoneLayer,
  IUploadedLayer,
  SelectedZoneLayer,
  SurfaceMapLayer,
} from '../Layer/DatasetZoneLayer';
import {
  ExpandInSituLayer,
  FWEPointLayer,
  InSituPointLayer,
  LabelLayer,
  MarkerLayer,
} from '../Layer/MarkerLayer';
import { MeshWMSLayer } from '../Layer/MeshWMSLayer';
import { Measurement } from './useMapMeasurement';

interface MapLayersReturn {
  layer: any[];
}

interface MeasurementPointData {
  index: number;
  position: [number, number];
}

/**
 * Custom hook to manage map layers
 *
 */
export function useMapLayers(
  meshUrl: IWmsProps | undefined,
  setMeshUrl: (item?: IWmsProps) => void,
  expandedPoints: IInsituPoint[] | null,
  measurement?: Measurement,
  onMeasurementDragStart?: (index: number) => void,
  onMeasurementDrag?: (index: number, point: [number, number]) => void,
  onMeasurementDragEnd?: () => void
): MapLayersReturn {
  const [layer, setLayer] = useState<any[]>([]);

  const markers = useMarkerStore((state) => state.markers);
  const point = useActiveMarker();
  const updateMarker = useMarkerStore((state) => state.updateMarker);

  const setUpdatedBounds = useMapStore((state) => state.setUpdatedBounds);
  const setDragPan = useMapStore((state) => state.setDragPan);
  const setCoordsMoved = useMapStore((state) => state.setCoordsMoved);

  // ---- datasets visibility:
  const showWavesZones = useLegendStore((state) => state.showWavesZones);

  const showAtmosphereZones = useLegendStore((state) => state.showAtmosphereZones);

  const showOceanZones = useLegendStore((state) => state.showOceanZones);

  const showOcean3dZones = useLegendStore((state) => state.showOcean3dZones);

  //------------------------

  const showGlobalShapeFile = useLegendStore((state) => state.showGlobalShapeFile);
  const showLabelLayer = useLegendStore((state) => state.showLabelLayer);
  const uploadedFeatures = useLegendStore((state) => state.uploadedFeatures);
  const showSurfaceMap = useLegendStore((state) => state.showSurfaceMap);
  const visibleUploads = useLegendStore((state) => state.visibleUploads);
  const uploadedShapefile = useLegendStore((state) => state.uploadedShapefile);
  const showUploadedShapefile = useLegendStore((state) => state.showUploadedShapefile);
  const setUploadingShapefile = useLegendStore((state) => state.setUploadingShapefile);
  const showInSituPoints = useLegendStore((state) => state.showInSituPoints);
  const showFWEPoints = useLegendStore((state) => state.showFWEPoints);

  const isMeasureMode = useLegendStore((state) => state.isMeasureMode);

  const datasetZones = useMapLayerStore((state) => state.datasetZones);
  const fweModelDomains = useMapLayerStore((state) => state.fweModelDomains);
  const globalShapeFile = useMapLayerStore((state) => state.globalShapeFile);

  const inSituPoints = usePointStore((state) => state.inSituPoints);
  const fwePoints = usePointStore((state) => state.fwePoints);

  const onDrag = (info: PickInfo<IMarker>, e: any) => {};
  const onDragStart = (info: PickInfo<IMarker>, e: any) => {
    setDragPan(false);
  };
  const onDragEnd = (info: PickInfo<IMarker>, e: any) => {
    if (
      point &&
      info.coordinate &&
      info.coordinate.length === 2 &&
      info.object &&
      info.object.active
    ) {
      updateMarker(point.id, {
        coordinatesToMove: [toFixed(info.coordinate[0], 6), toFixed(info.coordinate[1], 6)],
      });
      setCoordsMoved(true);
    }
    setDragPan(true);
  };

  /**
   * Creates dataset zone and FWE layers based on visibility status
   */
  const createBasicLayers = useCallback((): IPolygonLayer[] => {
    const polygonLayers: IPolygonLayer[] = [];

    if (showWavesZones) {
      polygonLayers.push(...datasetZones.filter((zone) => zone.category === DatasetType.SW));
    }

    if (showAtmosphereZones) {
      polygonLayers.push(...datasetZones.filter((zone) => zone.category === DatasetType.Wind));
    }

    if (showOceanZones) {
      polygonLayers.push(...datasetZones.filter((zone) => zone.category === DatasetType.HD));
    }

    if (showOcean3dZones) {
      polygonLayers.push(...datasetZones.filter((zone) => zone.category === DatasetType.HD_3D));
    }

    if (showFWEPoints) {
      polygonLayers.push(...fweModelDomains);
    }

    point?.shapeFiles.forEach((shape) => {
      if (shape.IsDisplay && shape.data) {
        polygonLayers.push(...shape.data);
      }
    });

    return polygonLayers;
  }, [
    showFWEPoints,
    datasetZones,
    showOcean3dZones,
    showOceanZones,
    showWavesZones,
    showAtmosphereZones,
    fweModelDomains,
    point?.shapeFiles,
  ]);

  /**
   * Creates selected zone layer with appropriate coloring
   */
  const createSelectedZoneLayer = useCallback((): IPolygonLayer[] => {
    const selectedPolygonLayer: IPolygonLayer[] = [];

    if (!point?.dataset) return selectedPolygonLayer;

    const lineColor = ConvertHexToRGBA(selectedColor);
    const filledColor = ConvertHexToRGBA(selectedColor, 0.2);

    datasetZones.forEach((zone) => {
      if (zone.id === point.dataset) {
        selectedPolygonLayer.push({
          id: zone.id,
          coordinates: zone.coordinates,
          lineColor: lineColor,
          color: filledColor,
        });
      }
    });

    return selectedPolygonLayer;
  }, [datasetZones, point?.dataset]);

  /**
   * Retrieves surface map data based on the active point
   */
  const getSurfaceMapData = useCallback((): {
    data: IPolygonLayer[];
    shouldUpdateBounds: boolean;
  } => {
    let surfaceMapLayerData: IPolygonLayer[] = [];
    let shouldUpdateBounds = false;

    if (
      point &&
      showSurfaceMap &&
      point.surfaceMaps.selectedLevel1 !== '' &&
      point.surfaceMaps.selectedLevel2 !== ''
    ) {
      const surfaceMap = point.surfaceMaps.overlays.find(
        (o) =>
          o.Level1 === point.surfaceMaps.selectedLevel1 &&
          o.Level2 === point.surfaceMaps.selectedLevel2
      );

      if (surfaceMap?.data) {
        surfaceMapLayerData = surfaceMap.data;
        shouldUpdateBounds = true;
      }
    }

    return { data: surfaceMapLayerData, shouldUpdateBounds };
  }, [point, showSurfaceMap]);

  /**
   * Creates labels for displayed markers
   */
  const createPointLabels = useMemo((): ILabelLayer[] => {
    if (!showLabelLayer) return [];

    return markers.map(
      (point): ILabelLayer => ({
        color: [142, 7, 77],
        name: point.label,
        coordinates: point.coordinates,
        active: point.active,
        textSize: 24,
        textAnchor: 'middle',
      })
    );
  }, [markers, showLabelLayer]);

  // *** Adding measure layer

  const createInProgressMeasurementLayer = (
    path: [[number, number], [number, number]],
    color: [number, number, number, number] = [0, 149, 255, 180]
  ) => {
    return new PathLayer({
      id: 'measure-draft-layer',
      data: [{ path }],
      getPath: (d: any) => d.path,
      getColor: color,
      getWidth: 2,
      widthMinPixels: 2,
      extensions: [new PathStyleExtension({ dash: true })],
      getDashArray: () => [4, 4],
      dashJustified: true,
    } as any);
  };

  const createMeasurementLineLayer = (path: [number, number][]) => {
    return new PathLayer({
      id: 'measure-line-layer',
      data: [{ path }],
      getPath: (d: any) => d.path,
      getColor: [0, 149, 255],
      getWidth: 2,
      widthMinPixels: 2,
    });
  };

  const createMeasurementPointLayer = useCallback(
    (points: [number, number][]) => {
      const data: MeasurementPointData[] = points.map((position, index) => ({
        index,
        position,
      }));

      return new ScatterplotLayer<MeasurementPointData>({
        id: 'measure-points-layer',
        data,
        pickable: true,
        getPosition: (d) => d.position as Position,
        getFillColor: [0, 149, 255],
        getLineColor: [255, 255, 255],
        getLineWidth: 1,
        lineWidthMinPixels: 1,
        getRadius: 5,
        radiusUnits: 'pixels',
        stroked: true,
        onDragStart: (info) => {
          if (typeof info.object?.index === 'number') {
            setDragPan(false);
            onMeasurementDragStart?.(info.object.index);
          }
        },
        onDrag: (info) => {
          if (
            typeof info.object?.index === 'number' &&
            info.coordinate &&
            info.coordinate.length >= 2
          ) {
            onMeasurementDrag?.(info.object.index, [info.coordinate[0], info.coordinate[1]]);
          }
        },
        onDragEnd: (info) => {
          if (
            typeof info.object?.index === 'number' &&
            info.coordinate &&
            info.coordinate.length >= 2
          ) {
            onMeasurementDrag?.(info.object.index, [info.coordinate[0], info.coordinate[1]]);
          }

          setDragPan(true);
          onMeasurementDragEnd?.();
        },
      });
    },
    [onMeasurementDrag, onMeasurementDragEnd, onMeasurementDragStart, setDragPan]
  );

  const createDistanceTooltipLayer = ({
    points,
    draftPoint,
    isClosed,
    totalDistance,
  }: {
    points: [number, number][];
    draftPoint: [number, number] | null;
    isClosed: boolean;
    totalDistance: number;
  }) => {
    if (isClosed || points.length === 0) {
      return;
    }

    let total = totalDistance;
    let anchorPoint: [number, number] = points[points.length - 1];

    if (draftPoint && points.length >= 1) {
      const from = points[points.length - 1];
      total += distance(turfPoint(from), turfPoint(draftPoint), {
        units: 'kilometers',
      });
      anchorPoint = [(from[0] + draftPoint[0]) / 2, (from[1] + draftPoint[1]) / 2];
    }

    if (total <= 0) {
      return;
    }

    return new TextLayer({
      id: 'measure-distance-label',
      data: [{ position: anchorPoint, text: `${total.toFixed(2)} km` }],
      getPosition: (d: any) => d.position,
      getText: (d: any) => d.text,
      getColor: [20, 20, 20],
      background: true,
      getBackgroundColor: [255, 255, 255, 250],
      backgroundPadding: [6, 3],
      getSize: 13,
      sizeUnits: 'pixels',
      getPixelOffset: () => [0, -10],
      getTextAnchor: 'middle',
      getAlignmentBaseline: 'bottom',
      billboard: true,
      pickable: false,
    });
  };

  const createMeasurementLayers = useCallback(
    (measurement?: Measurement): Layer<any>[] => {
      if (!measurement || measurement.points.length === 0) {
        return [];
      }

      const layers: Layer<any>[] = [];
      const { points, draftPoint, isClosed, isSnapToFirst, totalDistance } = measurement;

      if (points.length >= 2) {
        const committedPath = isClosed ? [...points, points[0]] : points;
        layers.push(createMeasurementLineLayer(committedPath));
      }

      if (!isClosed && points.length >= 1 && draftPoint) {
        const draftPath: [[number, number], [number, number]] = [
          points[points.length - 1],
          draftPoint,
        ];
        const draftColor: [number, number, number, number] = isSnapToFirst
          ? [0, 200, 100, 200]
          : [0, 149, 255, 180];
        layers.push(createInProgressMeasurementLayer(draftPath, draftColor));
      }

      if (isClosed && points.length >= 3) {
        layers.push(
          new PolygonLayer({
            id: 'measure-polygon-fill',
            data: [{ polygon: points }],
            getPolygon: (d: any) => d.polygon,
            getFillColor: [0, 149, 255, 35],
            getLineColor: [0, 149, 255],
            getLineWidth: 2,
            lineWidthMinPixels: 2,
            stroked: true,
            filled: true,
            pickable: false,
          })
        );
      }

      const measurePointLayer = createMeasurementPointLayer(points);
      layers.push(measurePointLayer);

      const distanceTooltipLayer = createDistanceTooltipLayer({
        points,
        draftPoint,
        isClosed,
        totalDistance,
      });

      if (distanceTooltipLayer) {
        layers.push(distanceTooltipLayer);
      }

      return layers;
    },
    [createMeasurementPointLayer]
  );

  /**
   * AppContainer function to update all map layers
   */
  const updateMapLayers = useCallback((): void => {
    const features: IUploadedLayer[] =
      showUploadedShapefile && uploadedFeatures ? uploadedFeatures : [];

    if (showUploadedShapefile && uploadedFeatures && uploadedFeatures) {
      setUploadingShapefile(false);
    }

    const polygonLayers = createBasicLayers();
    const selectedPolygonLayer = createSelectedZoneLayer();

    const measureLayers = isMeasureMode ? createMeasurementLayers(measurement) : [];

    const { data: surfaceMapLayerData, shouldUpdateBounds } = getSurfaceMapData();

    setUpdatedBounds(shouldUpdateBounds);

    const mapLayersArray = [
      SurfaceMapLayer(surfaceMapLayerData) as Layer<any, any>,
      MeshWMSLayer({ ...meshUrl } as IWmsProps, setMeshUrl) as any,
      DatasetZoneLayer(polygonLayers) as Layer<any, any>,
      GlobalDatasetZoneLayer(showGlobalShapeFile ? globalShapeFile : []) as Layer<any, any>,
      SelectedZoneLayer(selectedPolygonLayer) as Layer<any, any>,
      FWEPointLayer(showFWEPoints ? fwePoints : []) as Layer<any, any>,
      ExpandInSituLayer(showInSituPoints && expandedPoints ? expandedPoints : []),
      InSituPointLayer(showInSituPoints ? inSituPoints : []) as Layer<any, any>,
      ...(FeatureCollectionLayer(features, visibleUploads) as Array<Layer<any, any>>),
      ...measureLayers,
    ];

    const mapLayers = mapLayersArray.filter(Boolean) as any[];

    const featureCollectionLayers = FeatureCollectionLayer(features, visibleUploads);

    const graticuleLabels: ILabelLayer[] = [];
    const pointLabels = createPointLabels;

    const labelLayer = LabelLayer([...graticuleLabels, ...pointLabels]);

    const markerLayers = showLabelLayer ? MarkerLayer(markers, onDrag, onDragStart, onDragEnd) : [];

    setLayer([
      ...mapLayers,
      ...(featureCollectionLayers as any[]),
      labelLayer as any,
      ...(markerLayers as any[]),
    ]);
  }, [
    showUploadedShapefile,
    showLabelLayer,
    showFWEPoints,
    showInSituPoints,
    showGlobalShapeFile,
    markers,
    datasetZones,
    point,
    uploadedShapefile,
    uploadedFeatures,
    visibleUploads,
    fwePoints,
    inSituPoints,
    fweModelDomains,
    meshUrl,
    globalShapeFile,
    expandedPoints,
    isMeasureMode,
    measurement,
    createMeasurementLayers,
    onMeasurementDrag,
    onMeasurementDragEnd,
    onMeasurementDragStart,
    // these deps are incorrect, but the whole function works incorrectly - so it's only sad workaround to fit current solution
    showAtmosphereZones,
    showOceanZones,
    showWavesZones,
    showOcean3dZones,
  ]);

  useEffect(() => {
    updateMapLayers();
  }, [updateMapLayers]);

  return { layer };
}
